import {partition} from '@benchling/js-utils';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {MonomerNaturalAnalogue} from 'graphql-client/__generated/graphqlGlobalTypes';
import {readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {MonomerGraphQL} from 'molbio/monomers/__generated/MonomerGraphQL';
import {MonomerFragment} from 'molbio/monomers/MonomerConstants';
import * as FilterUtil from 'search/FilterUtil';
import {
  Filter,
  FilterErrors,
  FilterKey,
  isMonomerFilter,
  MonomerFilter,
  MonomerFilterQualifier,
} from 'search/SearchConstants';

function monomerErrorForTypes(monomer: MonomerGraphQL, types: Array<String>): string | undefined {
  if (!monomer.isNatural) {
    return undefined;
  }
  if (types.includes(ModelTypeConstants.RNA_OLIGO)) {
    if ([MonomerNaturalAnalogue.R_LOWER, MonomerNaturalAnalogue.U].includes(monomer.naturalAnalogue)) {
      return `${monomer.name} monomer can only be used when querying for DNA Oligo type.`;
    }
  }
  if (types.includes(ModelTypeConstants.DNA_OLIGO)) {
    if ([MonomerNaturalAnalogue.D_LOWER, MonomerNaturalAnalogue.T].includes(monomer.naturalAnalogue)) {
      return `${monomer.name} monomer can only be used when querying for RNA Oligo type.`;
    }
  }
  return undefined;
}

export default function validateMonomerFilter(filters: Array<Filter>): FilterErrors {
  const typesFilter = FilterUtil.getFilterByKey(filters, FilterKey.TYPES);
  const monomerFilters = FilterUtil.getFiltersByKey(filters, FilterKey.MONOMER);
  return filters.map((filter) => {
    if (isMonomerFilter(filter)) {
      if (monomerFilters.length > 1 && filter !== monomerFilters[0]) {
        return 'Only one monomer filter can be applied at a time';
      }
      if (filter.value && typesFilter?.value) {
        const monomer = readFragmentFromCache(MonomerFragment, filter.value);
        if (monomer && monomerErrorForTypes(monomer, typesFilter.value as Array<string>)) {
          return monomerErrorForTypes(monomer, typesFilter.value as Array<string>)!;
        }
      }
      if (filter.qualifier !== MonomerFilterQualifier.ANY_POS && !filter.qualifierValue) {
        return filter.qualifier === MonomerFilterQualifier.AT_POS
          ? `Must specify at least one position`
          : 'Must specify a count';
      }
    }
    return null;
  });
}

export function partitionTypesForMonomerFilter(
  filter: MonomerFilter,
  currentTypes: Array<string>
): [Array<string>, Array<string>] {
  if (filter.value) {
    const monomer = readFragmentFromCache(MonomerFragment, filter.value);
    if (
      monomer?.isNatural &&
      [MonomerNaturalAnalogue.R_LOWER, MonomerNaturalAnalogue.U].includes(monomer.naturalAnalogue)
    ) {
      return partition(currentTypes, (t) => t !== ModelTypeConstants.RNA_OLIGO);
    }
    if (
      monomer?.isNatural &&
      [MonomerNaturalAnalogue.D_LOWER, MonomerNaturalAnalogue.T].includes(monomer.naturalAnalogue)
    ) {
      return partition(currentTypes, (t) => t !== ModelTypeConstants.DNA_OLIGO);
    }
  }

  return [currentTypes, []];
}
